import { Api } from "fetch-run";
import { createQuery } from "./query";
import { createOperation, createRouteOperation } from "./mutation";
import {
  createListQuery,
  createReadQuery,
  createSearchQuery,
} from "./resource";
import { invalidateQuery, invalidateQueries, resetQueries } from "./store";

export function createApiStore(api: Api) {
  const ns = api.baseUrl;

  return {
    createQuery<Res extends object>(
      id: string,
      execute: () => Promise<Res | undefined>,
    ) {
      return createQuery(ns, id, execute);
    },

    createOperation,

    createRouteOperation,

    route<R extends string>(route: R) {
      return {
        create<Req extends object | void, Res extends object | void>() {
          return createRouteOperation<R, Req, Res>(route, (url, req: Req) =>
            api.post<Res, Req>(url, req),
          );
        },
        read<Item extends object>() {
          return createReadQuery<R, Item>(ns, route, (url) =>
            api.get<Item>(url),
          );
        },
        update<Req extends object | void, Res extends object | void>() {
          return createRouteOperation<R, Req, Res>(
            `${route}/:id` as R,
            (url, req: Req) => api.put<Res, Req>(url, req),
          );
        },
        delete<Res extends object | void = void>() {
          return createRouteOperation<R, void, Res>(
            `${route}/:id` as R,
            (url) => api.delete<Res>(url),
          );
        },
        list<ListItem extends object>() {
          return createListQuery<R, ListItem[]>(ns, route, (url) =>
            api.get<ListItem[]>(url),
          );
        },
        search<ListItem extends object>() {
          return createSearchQuery<R, ListItem[]>(ns, route, (url) =>
            api.get<ListItem[]>(url),
          );
        },
      };
    },

    invalidateQueries() {
      invalidateQueries(ns);
    },

    invalidateQuery(id: string) {
      invalidateQuery(ns, id);
    },

    resetQueries() {
      resetQueries(ns);
    },
  };
}

// README.md

// const api = Api.create("");

// const apiStore = createApiStore(api);

// type User = { id: number; name: string };
// const userRoute = apiStore.route("users");
// const useUser = userRoute.read<User>();
// const useUsers = userRoute.list<User>();
// const useCreateUser = userRoute.create<User, void>();

// type Post = { id: number; title: string };
// const postRoute = apiStore.route("users/:userId/posts");
// const useUserPost = postRoute.read<Post>();

// type Comment = { id: number; text: string };
// const userPostComment = apiStore.route("users/:userId/posts/:postId/comments");

// const useUserPostComment = userPostComment.read<Comment>();

// function Example() {
//   const { data: user } = useUser(123);
//   const { data: post } = useUserPost(123, { userId: 111 });
//   const { data: comment } = useUserPostComment(123, {
//     userId: "bar",
//     postId: 789,
//   });
// }
